import feeService from 'ApiService/Accountant/feeService';
import courseService from 'ApiService/Admin/courseService';
import adminFeeService from 'ApiService/Admin/feeService';
import batchService from 'ApiService/Admin/batchService';

import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';

import StudentFeeDetails from './StudentFeeDetails';
import SelectClass from 'pages/Accountant/Fees/PayFees/Steps/StepOne';

const Index = () => {
  /* ================= STEP ================= */
  const [currentStep, setCurrentStep] = useState(1);

  /* ================= CLASS DATA ================= */
  const [classData, setClassData] = useState({
    courseId: '',
    batchStructureId: '',
    batchId: '',
  });

  /* ================= DATA ================= */
  const [courses, setCourses] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const [students, setStudents] = useState([]);

  /* ================= UI ================= */
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [disable, setDisable] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingStudent, setLoadingStudent] = useState(false);

  /* ================= AUTH ================= */
  const { state } = useContext(AuthContext);
  const role = state?.user?.role;
  const isAccount = role === 'account';
  const isSuperAdmin = role === 'superadmin';

  const selectedBranch = isSuperAdmin
    ? state?.selectedBranch
    : state?.user?.branch?._id;

  /* ================= RESET ================= */
  const resetStepOne = () => {
    setClassData({
      courseId: '',
      batchStructureId: '',
      batchId: '',
    });
    setBatchStructures([]);
    setStudents([]);
    setSelectedStudent(null);
    setDisable(true);
  };

  /* ================= FETCH COURSES ================= */
  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      let res;

      if (isAccount) {
        res = await feeService.getCourseBatchInfo();
        setCourses(res?.data?.courses || []);
      } else {
        res = await courseService.getAllCourses(selectedBranch);
        setCourses(res?.data?.courses || []);
      }
    } catch {
      toast.error('Failed to fetch courses');
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    if (!state?.user) return;
    if (isSuperAdmin && !selectedBranch) return;

    resetStepOne();
    fetchCourses();
  }, [isSuperAdmin, selectedBranch, state?.user]);

  /* ================= FETCH STRUCTURES ================= */
  const fetchBatchStructures = async (courseId) => {
    try {
      const res = await batchService.getBatchStructuresByCourse(
        courseId,
        selectedBranch
      );
      setBatchStructures(res?.data?.batchStructures || []);
    } catch {
      toast.error('Failed to fetch batch structures');
      setBatchStructures([]);
    }
  };

  /* ================= FETCH BATCHES ================= */
  const fetchBatchesByStructure = async (structureId) => {
    try {
      const res = await batchService.getBatchesByBatchStructure(
        structureId,
        selectedBranch
      );
      return res?.data?.batches || [];
    } catch {
      toast.error('Failed to fetch batches');
      return [];
    }
  };

  /* ================= FETCH STUDENTS ================= */
  const fetchStudents = async () => {
    setLoadingStudent(true);
    try {
      let res;

      if (isAccount) {
        res = await feeService.getStudentDetails(classData);
      } else {
        res = await adminFeeService.getBatchStudentPaymentDetails(
          classData.batchId
        );
      }

      if (res?.data?.success) {
        setStudents(res.data.data || []);
      } else {
        toast.error('Failed to fetch students');
      }
    } catch {
      toast.error('Failed to fetch students');
    } finally {
      setLoadingStudent(false);
    }
  };

  /* ================= STEP HANDLERS ================= */
  const handleNext = async () => {
    if (currentStep === 1) {
      await fetchStudents();
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    setSelectedStudent(null);
  };

  const clearData = () => setStudents([]);

  /* ================= STEP VIEW ================= */
  const displayStep = () => {
    if (currentStep === 1) {
      return (
        <SelectClass
          setDisable={setDisable}
          classData={classData}
          setClassData={setClassData}
          courses={courses}
          loadingCourses={loadingCourses}
          batchStructures={batchStructures}
          fetchBatchStructures={fetchBatchStructures}
          fetchBatchesByStructure={fetchBatchesByStructure}
          selectedBranch={selectedBranch}
          clearData={clearData}
        />
      );
    }

    return (
      <StudentFeeDetails
        students={students}
        selectedStudent={selectedStudent}
        setSelectedStudent={setSelectedStudent}
        classData={classData}
        loadingStudent={loadingStudent}
      />
    );
  };

  /* ================= UI ================= */
  return (
    <div className="mt-3 h-full w-full">
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600" />

      <div className="mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
        <Card extra="-mt-[280px] w-full md:-mt-[340px] md:px-[40px] pb-3">
          <UseContextProvider>{displayStep()}</UseContextProvider>

          <div className="flex justify-end px-3 pt-4">
            {currentStep === 2 && (
              <button
                onClick={handleBack}
                className="mr-3 rounded-xl bg-gray-200 px-14 py-2"
              >
                Back
              </button>
            )}

            {currentStep === 1 && (
              <button
                disabled={disable || loadingStudent}
                onClick={handleNext}
                className="rounded-xl bg-brand-900 px-16 py-2 text-white disabled:opacity-60"
              >
                {loadingStudent ? 'Loading...' : 'Next'}
              </button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Index;
